import { useEffect, useRef } from "react";
import {
    Loader2, Activity, Users, User, Cpu
} from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
    ResponsiveContainer, CartesianGrid
} from "recharts";
import {
    formatBytes, formatTime, CustomTooltip
} from "./FirewallLogs";

interface UsersTabProps {
    usersData: any;
    usersLoading: boolean;
    onRefresh?: () => void;
}

export default function UsersTab({
    usersData,
    usersLoading,
    onRefresh
}: UsersTabProps) {
    const refreshRef = useRef(onRefresh);
    useEffect(() => {
        refreshRef.current = onRefresh;
    }, [onRefresh]);

    // Poll users API every 10 seconds while on Users tab
    useEffect(() => {
        const intervalId = setInterval(() => {
            refreshRef.current?.();
        }, 10000);

        return () => clearInterval(intervalId);
    }, []);
    return (
        <div className="animate-fade-in">
            {usersLoading ? (
                <div className="flex items-center justify-center py-24 gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Loading users data...</span>
                </div>
            ) : !usersData ? (
                <div className="flex items-center justify-center py-24 text-xs text-slate-500 font-bold uppercase tracking-wider">No Users Data Available</div>
            ) : (
                <>
                    {/* Top Sources by Bandwidth Chart */}
                    <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 mb-5 shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
                        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2 mb-3">
                            <h2 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest flex items-center gap-1.5">
                                <Activity size={12} className="text-purple-500" /> Top Sources by Bandwidth — Today
                            </h2>
                            <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                                <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Sent</div>
                                <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Received</div>
                            </div>
                        </div>
                        <div className="h-[220px] w-full">
                            {!usersData?.list?.length ? (
                                <div className="h-full flex items-center justify-center text-xs text-slate-500 font-bold uppercase">No Data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={usersData.list.slice(0, 10).map((u: any) => ({ name: u.username || u.ip || '—', sent: u.sent, rcvd: u.rcvd }))} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                        <defs>
                                            <linearGradient id="sentBwGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#c084fc" stopOpacity={0.85} />
                                                <stop offset="100%" stopColor="#7e22ce" stopOpacity={0.25} />
                                            </linearGradient>
                                            <linearGradient id="rcvdBwGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.85} />
                                                <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.25} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                                        <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 7 }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={40} />
                                        <YAxis tickFormatter={(v: number) => formatBytes(v)} tick={{ fill: '#94a3b8', fontSize: 8 }} axisLine={false} tickLine={false} />
                                        <RechartsTooltip content={<CustomTooltip />} />
                                        <Bar dataKey="sent" name="Sent" fill="url(#sentBwGrad)" radius={[3, 3, 0, 0]} barSize={10} />
                                        <Bar dataKey="rcvd" name="Received" fill="url(#rcvdBwGrad)" radius={[3, 3, 0, 0]} barSize={10} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* All Users & Devices Table */}
                    <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800/80 rounded-xl overflow-hidden shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)] mb-5">
                        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-2">
                            <h2 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest flex items-center gap-1.5">
                                <Users size={12} className="text-emerald-500" /> All Users & Devices — Today's Activity
                            </h2>
                            <div className="flex items-center gap-3 text-[9px] font-bold uppercase">
                                <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">USER</span>
                                <span className="text-slate-500">= Authenticated</span>
                                <span className="px-2 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">IP ONLY</span>
                                <span className="text-slate-500">= No username</span>
                            </div>
                        </div>
                        <div className="overflow-x-auto custom-scroll max-h-[400px]">
                            <table className="w-full text-xs text-left min-w-[900px]">
                                <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 uppercase tracking-widest text-[9px] font-bold sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="px-4 py-2.5">Type</th>
                                        <th className="px-4 py-2.5">Identity</th>
                                        <th className="px-4 py-2.5">Device / Hostname</th>
                                        <th className="px-4 py-2.5">OS</th>
                                        <th className="px-4 py-2.5 text-right">Sessions</th>
                                        <th className="px-4 py-2.5 text-right">Sent</th>
                                        <th className="px-4 py-2.5 text-right">Received</th>
                                        <th className="px-4 py-2.5 text-right">Total</th>
                                        <th className="px-4 py-2.5 text-right">Apps</th>
                                        <th className="px-4 py-2.5 text-right">Dests</th>
                                        <th className="px-4 py-2.5">Last Seen</th>
                                        <th className="px-4 py-2.5">Bandwidth</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/50">
                                    {!usersData?.list?.length ? (
                                        <tr><td colSpan={12} className="py-12 text-center text-xs text-slate-500 font-bold uppercase">No Users Data</td></tr>
                                    ) : (
                                        (() => {
                                            const maxBw = Math.max(...usersData.list.map((u: any) => (u.sent || 0) + (u.rcvd || 0)), 1);
                                            return usersData.list.map((u: any, i: number) => {
                                                const isUser = u.source_type === 'user';
                                                const totalBw = (u.sent || 0) + (u.rcvd || 0);
                                                return (
                                                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                                                        <td className="px-4 py-2">
                                                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase border ${isUser ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' : 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20'}`}>
                                                                {isUser ? 'USER' : 'IP'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <div className="flex items-center gap-1.5">
                                                                <User size={11} className={isUser ? 'text-emerald-500' : 'text-slate-400 dark:text-slate-500'} />
                                                                <span className={`font-bold ${isUser ? 'text-gray-900 dark:text-white' : 'text-cyan-600 dark:text-cyan-400 font-mono'}`}>
                                                                    {isUser ? u.username : u.ip || '—'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2 text-slate-700 dark:text-slate-300 font-semibold truncate max-w-[150px]">{u.primary_device || '—'}</td>
                                                        <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{u.primary_os || '—'}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-gray-900 dark:text-white font-bold">{u.sessions?.toLocaleString()}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-blue-600 dark:text-blue-400">{formatBytes(u.sent)}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-emerald-600 dark:text-emerald-400">{formatBytes(u.rcvd)}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-gray-900 dark:text-white font-bold">{formatBytes(totalBw)}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-slate-600 dark:text-slate-400">{u.app_count}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-slate-600 dark:text-slate-400">{u.dest_count}</td>
                                                        <td className="px-4 py-2 text-slate-500 font-mono whitespace-nowrap">{formatTime(u.last_seen)}</td>
                                                        <td className="px-4 py-2 w-28">
                                                            <div className="w-full bg-slate-100 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-900 rounded-full h-2 overflow-hidden">
                                                                <div className="h-full rounded-full bg-gradient-to-r from-purple-500/20 to-purple-500/80" style={{ width: `${Math.max((totalBw / maxBw) * 100, 2)}%` }} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Top Applications per Source */}
                    <div className="bg-white dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800/80 rounded-xl overflow-hidden shadow-sm dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)] mb-5">
                        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
                            <Cpu size={12} className="text-purple-500" />
                            <h2 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-widest">Top Applications per Source</h2>
                        </div>
                        <div className="overflow-x-auto custom-scroll max-h-[350px]">
                            <table className="w-full text-xs text-left min-w-[700px]">
                                <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 uppercase tracking-widest text-[9px] font-bold sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="px-4 py-2.5">User / IP</th>
                                        <th className="px-4 py-2.5">Application</th>
                                        <th className="px-4 py-2.5 text-right">Sessions</th>
                                        <th className="px-4 py-2.5 text-right">Bandwidth</th>
                                        <th className="px-4 py-2.5">Volume</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/50">
                                    {!usersData?.top_apps?.length ? (
                                        <tr><td colSpan={5} className="py-12 text-center text-xs text-slate-500 font-bold uppercase">No Application Data</td></tr>
                                    ) : (
                                        (() => {
                                            const maxBw = usersData.top_apps[0]?.bandwidth || 1;
                                            return usersData.top_apps.slice(0, 50).map((a: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                                                    <td className="px-4 py-2">
                                                        <span className={`font-bold ${a.username ? 'text-pink-600 dark:text-pink-400' : 'text-cyan-600 dark:text-cyan-400 font-mono'}`}>
                                                            {a.username || a.ip || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 text-purple-600 dark:text-purple-400 font-bold font-mono">{a.app}</td>
                                                    <td className="px-4 py-2 text-right font-mono text-gray-900 dark:text-white font-bold">{a.sessions?.toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-right font-mono text-blue-600 dark:text-blue-400">{formatBytes(a.bandwidth)}</td>
                                                    <td className="px-4 py-2 w-28">
                                                        <div className="w-full bg-slate-100 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-900 rounded-full h-2 overflow-hidden">
                                                            <div className="h-full rounded-full bg-gradient-to-r from-purple-500/15 to-purple-500/85" style={{ width: `${Math.max((a.bandwidth / maxBw) * 100, 2)}%` }} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ));
                                        })()
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
